/**
 * Refunds owed to customers that did not go through on their own.
 *
 * Every automatic refund — a cancellation, a return, a payment that landed on
 * an order already cancelled — ends here if the gateway refuses it or it
 * fails before reaching the gateway. The system retries each one up to three
 * times, an hour apart; this is where the rest are finished: retried by hand,
 * or closed once paid another way.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import type { RefundTask } from '@terra/api-client';
import { Skeleton } from '@terra/ui';

import { api } from '../../lib/api';
import { formatPaise } from '../../lib/money';

const SOURCE_LABEL: Record<RefundTask['source'], string> = {
  ORDER_CANCELLED: 'Order cancelled',
  PACKAGE_CANCELLED: 'Item cancelled',
  RETURN: 'Return',
  LATE_PAYMENT: 'Paid after cancel',
  GATEWAY_FAILED: 'Bank refused',
};

const KEY = 'admin-refund-tasks';

export function RefundsOwedPanel() {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const tasks = useQuery({
    queryKey: [KEY],
    queryFn: () => api.settlements.listRefundTasks({ status: 'OPEN', page: 1, pageSize: 50 }),
  });

  const failed = (err: unknown) =>
    setError(
      (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
        ?.message ?? 'That did not work.',
    );

  const retry = useMutation({
    mutationFn: (id: string) => api.settlements.retryRefundTask(id),
    onSuccess: (task) => {
      setError(
        task.status === 'OPEN' ? `Still failing: ${task.lastError ?? 'unknown error'}` : null,
      );
      void qc.invalidateQueries({ queryKey: [KEY] });
    },
    onError: failed,
  });

  const resolve = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) =>
      api.settlements.resolveRefundTask(id, note),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: [KEY] });
    },
    onError: failed,
  });

  const total = tasks.data?.page.total ?? 0;
  if (!tasks.isPending && total === 0) return null;
  const owed = (tasks.data?.data ?? []).reduce((a, t) => a + t.amountPaise, 0);

  return (
    <section
      aria-label="Refunds owed"
      className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm"
    >
      <div className="border-border/60 flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4">
        <div>
          <h2 className="text-ink text-[14px] font-bold">Refunds owed to customers</h2>
          <p className="text-ink-muted mt-0.5 text-[12.5px] font-medium">
            Refunds that did not go through on their own. Each is retried automatically up to three
            times; retry it here, or mark it resolved once paid another way.
          </p>
        </div>
        <span className="rounded-md bg-red-50 px-2 py-1 text-[11.5px] font-bold text-red-700">
          {total} open · {formatPaise(owed)}
        </span>
      </div>

      {error && (
        <p role="alert" className="bg-red-50 px-5 py-2 text-[12.5px] font-semibold text-red-700">
          {error}
        </p>
      )}

      {tasks.isPending ? (
        <div className="space-y-2 p-5">
          {[0, 1].map((i) => (
            <Skeleton key={i} className="h-10 rounded-lg" />
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[13px]">
            <thead className="text-ink-subtle border-border/60 border-b text-[11px] font-semibold uppercase tracking-wider">
              <tr>
                <th className="px-5 py-3">Order</th>
                <th className="px-5 py-3">Customer</th>
                <th className="px-5 py-3">Why</th>
                <th className="px-5 py-3">Last error</th>
                <th className="px-5 py-3 text-right">Amount</th>
                <th className="px-5 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {(tasks.data?.data ?? []).map((t) => (
                <tr key={t.id} className="border-border/40 border-b last:border-0">
                  <td className="text-ink px-5 py-3 font-mono font-medium">
                    {t.orderNumber ?? t.orderGroupId.slice(0, 8)}
                  </td>
                  <td className="text-ink px-5 py-3">{t.customerName ?? '—'}</td>
                  <td className="text-ink px-5 py-3">
                    {SOURCE_LABEL[t.source] ?? t.source}
                    {t.payToUpiId && (
                      <span className="block text-[12px] font-bold text-sky-700">
                        Pay to UPI: {t.payToUpiId}
                      </span>
                    )}
                    <span className="text-ink-subtle block text-[11.5px]">
                      {new Date(t.createdAt).toLocaleString('en-IN')} · {t.attempts} tries
                    </span>
                  </td>
                  <td
                    className="text-ink-subtle max-w-[260px] truncate px-5 py-3"
                    title={t.lastError ?? ''}
                  >
                    {t.lastError ?? '—'}
                  </td>
                  <td className="text-ink px-5 py-3 text-right font-semibold">
                    {formatPaise(t.amountPaise)}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      {t.paymentId && (
                        <button
                          type="button"
                          disabled={retry.isPending}
                          onClick={() => retry.mutate(t.id)}
                          className="rounded-lg bg-amber-600 px-3 py-1.5 text-[12px] font-bold text-white hover:bg-amber-700 disabled:opacity-50"
                        >
                          Retry
                        </button>
                      )}
                      <button
                        type="button"
                        disabled={resolve.isPending}
                        onClick={() => {
                          const note = window.prompt(
                            'How was this refund paid? (e.g. NEFT UTR 1234 on 12 Oct)',
                          );
                          if (note && note.trim().length >= 3) {
                            resolve.mutate({ id: t.id, note: note.trim() });
                          }
                        }}
                        className="border-border rounded-lg border px-3 py-1.5 text-[12px] font-bold hover:bg-slate-50 disabled:opacity-50"
                      >
                        Mark paid
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
